import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// Redux
import { selectPatient } from "Store/PatientSlice";
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// MUI
import {
  Stack,
  Box,
  Tab,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";

// Components
import ViewMoreAppointmentsModal from "Components/Appointments/ViewMoreAppointmentsModal";
import EditAppointmentModal from "Components/Appointments/EditAppointmentModal";
import {
  getAvailableClinicSlots,
  hasAppointmentTimePassed,
  ClinicFilter,
} from "Components/Appointments/Common";
import { MNTabs, PatientInfoHeader, AppointmentCard } from "Common/Components";
import { ConfirmModal } from "Common/Modals/ConfirmModal";

// Resources
import { getAppointmentsByPatientId } from "API/Resource/appointment";
import { cancelAppointment } from "API/Resource/appointment";

// Styles
import { styles } from "../../../Appointments/styles";
import { styles as tabStyles } from "Common/Components/MNTabs/styles";

// Constants
import {
  APPOINTMENT_INACTIVE_STATUS,
  APPOINTMENT_STATUS,
} from "Constants/AppointmentConstants";
import { PATIENT_TYPE } from "Constants/PatientManagementConstants";
import { HALF_SECOND } from "Constants/TimeConstants";

const UpcomingPastAppointments = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const patient = useSelector(selectPatient);
  const patientId = patient?.id;

  const UPCOMING_APPOINTMENTS = "Upcoming Appointments";

  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [selectedTab, setSelectedTab] = useState(UPCOMING_APPOINTMENTS);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openConfirmEdit, setOpenConfirmEdit] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [openViewMoreModal, setOpenViewMoreModal] = useState(false);
  const [clinicSlots, setClinicSlots] = useState(null);
  const [newAppointment, setNewAppointment] = useState(null);
  const [openEditAppointmentModal, setOpenEditAppointmentModal] =
    useState(false);

  const [clinics, setClinics] = useState([]);
  const [openClinicPicker, setOpenClinicPicker] = useState(false);
  const [allAppointments, setAllAppointments] = useState([]);

  const noAppointmentsText =
    selectedTab === UPCOMING_APPOINTMENTS
      ? "upcomingpastappointments.noUpcomingAppointments"
      : "upcomingpastappointments.noPastAppointments";

  const openEditModal = (appointment) => {
    setSelectedAppointment(appointment);
    setOpenConfirmEdit(true);
  };

  const openDeleteModal = (appointment) => {
    setSelectedAppointment(appointment);
    setOpenConfirmDelete(true);
  };

  const refreshPatientAppointments = useCallback(async () => {
    if (!patientId) {
      setAppointments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await getAppointmentsByPatientId(patientId);
      setAppointments(result);
    } catch (error) {
      dispatch(
        openSnackbar({
          message: t("errors.errorRefreshingAppointments"),
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, patientId, t]);

  const deleteAppointment = async (appointment) => {
    setLoadingModal(true);
    try {
      await cancelAppointment(appointment.id);
      // update appointment status in the appointments list
      // to reflect the cancellation
      const updatedAppointments = appointments.map((appt) =>
        appt.id === appointment.id
          ? { ...appt, status: APPOINTMENT_STATUS.CancelledByPatient }
          : appt
      );
      setAppointments(updatedAppointments);
      setFilteredAppointments(updatedAppointments);
      // Reset the ClinicFilter
      setClinics([]);
    } catch (error) {
      const errorMessages = {
        APPOINTMENT_TIME_PASSED: t(
          "upcomingpastappointments.appointmentTimePassed"
        ),
      };
      const defaultErrorMessage = t("errors.errorCancellingAppointments");

      const errorMessage = errorMessages[error.message] || defaultErrorMessage;
      dispatch(openSnackbar({ message: errorMessage, severity: "error" }));
    } finally {
      setOpenConfirmDelete(false);
      setLoadingModal(false);
      handleTabChange(null, UPCOMING_APPOINTMENTS);
    }
  };

  // Fetch appointments for the patient
  useEffect(() => {
    refreshPatientAppointments();
  }, [refreshPatientAppointments]);

  // Split appointments into past and upcoming
  useEffect(() => {
    if (appointments.length === 0) {
      setAllAppointments([]);
      setFilteredAppointments([]);
      return;
    }

    let pastAppointments = [];
    let upcomingAppointments = [];

    // After sorting, separate into past and upcoming appointments
    appointments.forEach((appointment) => {
      if (APPOINTMENT_INACTIVE_STATUS.includes(appointment.status)) {
        pastAppointments.push(appointment);
        return;
      }

      if (hasAppointmentTimePassed(appointment.date, appointment.endTime)) {
        pastAppointments.push(appointment);
      } else {
        upcomingAppointments.push(appointment);
      }
    });

    setAllAppointments(
      selectedTab === UPCOMING_APPOINTMENTS
        ? upcomingAppointments
        : pastAppointments
    );

    const selectedClinicNames = clinics.map((clinic) => clinic.Clinic.name);

    if (clinics.length === 0) {
      setFilteredAppointments(
        selectedTab === UPCOMING_APPOINTMENTS
          ? upcomingAppointments
          : pastAppointments
      );
    } else {
      setFilteredAppointments(
        selectedTab === UPCOMING_APPOINTMENTS
          ? upcomingAppointments.filter((appointment) =>
              selectedClinicNames.includes(appointment.Clinic.name)
            )
          : pastAppointments.filter((appointment) =>
              selectedClinicNames.includes(appointment.Clinic.name)
            )
      );
    }
  }, [appointments, selectedTab, clinics]);

  const handleTabChange = (_, newValue) => {
    setLoading(true);
    setSelectedTab(newValue);
    setClinics([]);
    setTimeout(() => {
      setLoading(false);
    }, HALF_SECOND);
  };

  const getClinicAvailableSlots = async () => {
    try {
      setLoadingModal(true);
      const slots = await getAvailableClinicSlots({
        patient,
        appointment: selectedAppointment,
      });
      setClinicSlots(slots);
      setOpenViewMoreModal(true);
    } catch (error) {
      const errorMessages = {
        RESCHEDULE_TIME_PASSED: t(
          "upcomingpastappointments.rescheduleTimePassed"
        ),
      };

      const defaultErrorMessage = t("errors.errorReschedulingAppointments");
      const errorMessage = errorMessages[error.message] || defaultErrorMessage;

      dispatch(openSnackbar({ message: errorMessage, severity: "error" }));
    } finally {
      setOpenConfirmEdit(false);
      setOpenEditAppointmentModal(false);
      setLoadingModal(false);
    }
  };

  const createAppointment = (clinic, date, time) => {
    const appointment = {
      patientId: patient.id,
      patientType: PATIENT_TYPE.InSystem,
      clinicId: clinic.Clinic.id,
      clinicName: t("clinic.name", { clinic: clinic.Clinic }),
      clinicAddress: t("clinic.address", { clinic: clinic.Clinic }),
      date: date,
      startTime: time.startTime,
      endTime: time.endTime,
      type: selectedAppointment.type,
      notes: selectedAppointment.notes,
      accuroSlots: time.uniqueSlots,
    };
    setNewAppointment(appointment);
    setOpenEditAppointmentModal(true);
  };

  return (
    <Box sx={styles.container}>
      <PatientInfoHeader onBackClick={() => navigate("/")} patient={patient} />
      <MNTabs
        value={selectedTab}
        onChange={handleTabChange}
        tabName={"Appointment Tabs"}
      >
        <Tab
          label={t("upcomingpastappointments.upcoming appointments")}
          sx={tabStyles.MNTabStyle(selectedTab === UPCOMING_APPOINTMENTS)}
          value="Upcoming Appointments"
          id="upcoming-appointments-tab"
          aria-controls="upcoming-appointments-panel"
          aria-label="Upcoming Appointments Tab"
        />
        <Tab
          label={t("upcomingpastappointments.past appointments")}
          sx={tabStyles.MNTabStyle(selectedTab !== UPCOMING_APPOINTMENTS)}
          value="Past Appointments"
          id="past-appointments-tab"
          aria-controls="past-appointments-panel"
          aria-label="Past Appointments Tab"
        />
      </MNTabs>
      <Paper sx={styles.upcomingPastAppointmentPaper}>
        {allAppointments.length > 0 && (
          <Box sx={styles.searchClinicBox}>
            <ClinicFilter
              options={Array.from(
                new Map(
                  allAppointments.map((appointment) => [
                    appointment.Clinic.name,
                    appointment,
                  ])
                ).values()
              )}
              selectedClinics={clinics}
              setSelectedClinics={setClinics}
              open={openClinicPicker}
              setOpenPicker={setOpenClinicPicker}
            />
          </Box>
        )}
        <Stack spacing={2} direction="row" useFlexGap sx={styles.cardStack}>
          {loading ? (
            <CircularProgress color="primary" />
          ) : filteredAppointments.length === 0 ? (
            <Typography align="center" sx={{ p: 4 }}>
              {t(noAppointmentsText)}
            </Typography>
          ) : (
            filteredAppointments.map((appointmentSlot, index) => (
              <AppointmentCard
                key={index}
                appointmentSlot={appointmentSlot}
                selectedTab={selectedTab}
                openEditModal={openEditModal}
                openDeleteModal={openDeleteModal}
              />
            ))
          )}
        </Stack>
      </Paper>

      {/* Confirm Edit Modal */}
      <ConfirmModal
        open={openConfirmEdit}
        onClose={() => setOpenConfirmEdit(false)}
        onConfirm={getClinicAvailableSlots}
        loading={loadingModal}
        translationKey="confirmAptEditModal"
      />

      {/* Confirm Delete Modal */}
      <ConfirmModal
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        onConfirm={() => deleteAppointment(selectedAppointment)}
        loading={loadingModal}
        translationKey="confirmDeleteModal"
      />

      {openViewMoreModal && (
        <ViewMoreAppointmentsModal
          clinic={clinicSlots}
          openModal={openViewMoreModal}
          closeModal={() => setOpenViewMoreModal(false)}
          createAppointment={createAppointment}
        />
      )}
      {openEditAppointmentModal && (
        <EditAppointmentModal
          patient={patient}
          openModal={openEditAppointmentModal}
          closeModal={() => setOpenEditAppointmentModal(false)}
          newAppointment={newAppointment}
          currentAppointment={selectedAppointment}
          refreshAvailableSlots={getClinicAvailableSlots}
          refreshPatientAppointments={refreshPatientAppointments}
        />
      )}

      <Box component="footer" sx={styles.upcomingPastAppointmentFooterBox}>
        <Typography sx={styles.upcomingPastAppointmentNote}>
          {t("upcomingpastappointments.note")}
        </Typography>
      </Box>
    </Box>
  );
};

export default UpcomingPastAppointments;
